//External libs
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

//Local libs
import Service from 'src/app/common/service';
import AttachmentsModel from './attachments.model';

/**
 * @service AttachmentsService
 * @description Service for managing attachment file operations and metadata.
 * Provides HTTP methods for uploading, retrieving, and managing attachment files
 * associated with various AquaCloud entities (projects, records, documents, etc.).
 * Extends base Service class with generic type AttachmentsModel.
 *
 * @feature 1. Upload attachment files with metadata
 * @feature 2. Retrieve attachment files and associated metadata
 * @feature 3. Delete attachment files
 * @feature 4. Query attachments by entity type and ID
 * @feature 5. Manage file associations and relationships
 * @feature 6. Support multiple attachment formats (documents, images, PDFs)
 *
 * @architecture
 * Extends base Service<AttachmentsModel> providing REST API integration with
 * Media Management System (MMS) backend. Handles file upload/download operations
 * and attachment metadata management. Supports both single and batch file operations.
 * Implements request tracking and cancellation for cleanup.
 *
 * @service-endpoint /mms/attachments/
 * @service-provider root (singleton, application-wide)
 * @service-type Media Management Service
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 * @created 2025-01-19
 * @mms-integration Handles all attachment operations through Media Management System backend
 */
@Injectable({
  providedIn: 'root'
})
export default class AttachmentsService extends Service<AttachmentsModel> {

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes AttachmentsService by calling parent Service constructor with
   * HttpClient instance and service endpoint path (/mms/attachments/). Sets up
   * base URL configuration and HTTP options for attachment operations.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   *   Used for file upload, download, and attachment metadata operations
   *
   * @returns {void}
   *
   * @execution-flow
   * 1. Receive HttpClient dependency injection from Angular injector
   * 2. Call super() with httpClient and service endpoint path
   * 3. Parent Service class configures baseUrl and httpOptions
   * 4. Service ready for attachment operations on /mms/attachments/ endpoint
   *
   * @example
   * // Service automatically provided via dependency injection
   * constructor(private attachmentsService: AttachmentsService) {
   *   // Service initialized and ready for use
   * }
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/attachments/');
  }
}